import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'

/**
 * Fetch rows from a table filtered by the current user.
 * Returns { data, loading, error, refetch }
 */
export function useSupabaseQuery(table, options = {}) {
  const { user } = useAuth()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const {
    column = 'caregiver_id',
    orderBy = 'created_at',
    ascending = false,
    filters = [],
    select = '*',
    enabled = true,
  } = options

  const fetch = useCallback(async () => {
    if (!user || !enabled) {
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)

    try {
      let query = supabase
        .from(table)
        .select(select)
        .eq(column, user.id)
        .order(orderBy, { ascending })

      for (const [col, op, val] of filters) {
        if (op === 'eq') query = query.eq(col, val)
        else if (op === 'gte') query = query.gte(col, val)
        else if (op === 'lte') query = query.lte(col, val)
        else if (op === 'is') query = query.is(col, val)
      }

      const { data: result, error: err } = await query
      if (err) throw err
      setData(result || [])
    } catch (err) {
      console.error(`Error fetching ${table}:`, err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [user, table, select, column, orderBy, ascending, enabled, JSON.stringify(filters)])

  useEffect(() => { fetch() }, [fetch])

  return { data, loading, error, refetch: fetch }
}

/**
 * Insert a row into a table.
 */
export function useSupabaseInsert(table) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const insert = async (row) => {
    setLoading(true)
    setError(null)
    try {
      const { data, error: err } = await supabase
        .from(table)
        .insert(row)
        .select()
        .single()
      if (err) throw err
      return data
    } catch (err) {
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }

  return { insert, loading, error }
}

/**
 * Update a row in a table.
 */
export function useSupabaseUpdate(table) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const update = async (id, changes) => {
    setLoading(true)
    setError(null)
    try {
      const { data, error: err } = await supabase
        .from(table)
        .update(changes)
        .eq('id', id)
        .select()
        .single()
      if (err) throw err
      return data
    } catch (err) {
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }

  return { update, loading, error }
}

/**
 * Delete a row from a table.
 */
export function useSupabaseDelete(table) {
  const [loading, setLoading] = useState(false)

  const remove = async (id) => {
    setLoading(true)
    try {
      const { error: err } = await supabase
        .from(table)
        .delete()
        .eq('id', id)
      if (err) throw err
    } finally {
      setLoading(false)
    }
  }

  return { remove, loading }
}

/**
 * Format helpers used across features
 */
export function formatDate(dateStr) {
  if (!dateStr) return ''
  // A date-only string like "2026-08-13" is parsed by `new Date()` as UTC
  // midnight, which renders one day early in US time zones. Parse the parts
  // manually and build a LOCAL date so the calendar day is correct.
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr)
  const d = m
    ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    : new Date(dateStr)
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export function formatTime(dateStr) {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function formatDuration(clockIn, clockOut) {
  if (!clockIn || !clockOut) return '—'
  const ms = new Date(clockOut) - new Date(clockIn)
  const hours = Math.floor(ms / 3600000)
  const minutes = Math.floor((ms % 3600000) / 60000)
  if (hours === 0) return `${minutes}m`
  return `${hours}h ${minutes}m`
}

export function hoursFromMs(ms) {
  return Math.round((ms / 3600000) * 100) / 100
}
